import React, { useState, useEffect } from 'react';
import AdminUI from './AdminUI';
import './App.css';
import DBLogo from './assets/DBlogo.png';
import navLogo from './assets/logo.png';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { baseUrl } from './config/Config';
import { Pricing } from './pages/Pricing';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { Header } from './components/Header.js';

const ContactValidationSchema = Yup.object({
  name: Yup.string().required('Required'),
  email: Yup.string()
    .email('Invalid email address')
    .required('Required'),
  contact_number: Yup.string().required('Required').min(8,"Inavalid contact number"),
});
const loginValidationSchema = Yup.object({
  username: Yup.string().required('Required'),
  password: Yup.string().required('Required')
});

function App() {
  const [authStatus, setAuthStatus] = useState('checking'); // 'checking' | 'unauth' | 'auth'
  const [token, setToken] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const DOMAIN_REGEX = /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/i;

  // LOGIN
  // const [username, setUsername] = useState('');
  // const [password, setPassword] = useState('');
  const [showSignUp, setShowSignUp] = useState(false);
  const [showContactUs, setShowContactUs] = useState(false);

  // SIGNUP FIELDS
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [signupPasswordConfirm, setSignupPasswordConfirm] = useState('');
  const [company, setCompany] = useState(''); // will now hold company DOMAIN
  const [email, setEmail] = useState('');
  const [signUpSuccess, setSignUpSuccess] = useState(false);

  // Email verification
  const [verifyCode, setVerifyCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [codeStatus, setCodeStatus] = useState('');
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const isEmailValid = emailRegex.test(email);

  // App
  const [sites, setSites] = useState('');
  const [masterAds, setMasterAds] = useState('');
  const [results, setResults] = useState([]); // keep default []
  const [csvData, setCsvData] = useState('');
  const [isAppUrl, setIsAppUrl] = useState(false);

  // credits used (sites scanned)
  const [myUsage, setMyUsage] = useState(0);
  const [allowedSites, setAllowedSites] = useState(0);

  // filename shown next to the styled file button
  const [uploadName, setUploadName] = useState('');

  // NEW: banner shown on login screen for quota/expiry
  const [authBanner, setAuthBanner] = useState('');

  // normalize things like https://example.com/path -> example.com
  function extractDomain(input) {
    if (!input) return '';
    const raw = String(input).trim().toLowerCase().replace(/^@/, '');
    // if user pasted a URL, try to pull hostname
    try {
      const url = raw.includes('://') ? new URL(raw) : new URL(`http://${raw}`);
      return url.hostname.replace(/^www\./, '');
    } catch {
      return raw.replace(/^www\./, '');
    }
  }

  // STRICT VALIDATION ON LOAD
  useEffect(() => {
    const savedToken = localStorage.getItem('token');
    const savedUsername = localStorage.getItem('username');

    if (!savedToken) {
      setAuthStatus('unauth');
      return;
    }

    let canceled = false;
    (async () => {
      try {
        const meRes = await fetch(`${baseUrl}/me`, {
          headers: { Authorization: 'Bearer ' + savedToken }
        });

        if (meRes.status === 403) {
          // quota / duration ended
          const data = await meRes.json().catch(() => ({}));
          const msg = data?.meassage || 'You have exceeded the allowed capacity as a free user, please contact support@dollarbirdinc.com for your paid access.';
          if (!canceled) {
            localStorage.removeItem('token');
            localStorage.removeItem('username');
            setToken('');
            setIsAdmin(false);
            setAuthBanner(msg);
            setAuthStatus('unauth');
          }
          return;
        }

        if (!meRes.ok) {
          const errorData = await meRes.json();
          // console.log(errorData.message); 
          throw new Error(errorData?.message||'Invalid token');
            // throw new Error('Invalid token')
          };

        const me = await meRes.json();
        if (canceled) return;

        setToken(savedToken);
        setIsAdmin(Boolean(me.is_admin));
        await fetchMyUsage(savedToken);
        if (savedUsername !== me.username) {
          localStorage.setItem('username', me.username || savedUsername || '');
        }
        setAuthStatus('auth');
      } catch {
        if (canceled) return;
        localStorage.removeItem('token');
        localStorage.removeItem('username');
        setToken('');
        setIsAdmin(false);
        setAuthStatus('unauth');
      }
    })();

    return () => { canceled = true; };
  }, []);

  const fetchMyUsage = async (tk = token) => {
    if (!tk) return;
    try {
      const res = await fetch(`${baseUrl}/me/usage`, {
        headers: { Authorization: 'Bearer ' + tk }
      });
      if (!res.ok) return;
      const data = await res.json();
      setMyUsage(data?.site_count ?? 0);
      setAllowedSites(data?.allowed_sites ?? 0);
    } catch { /* ignore */ }
  };

  const handleLogin = async (values,restForm) => {
    try {
      // console.log("handleLogin values==>",values)
      // return
      const response = await fetch(`${baseUrl}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...values }),
      });
      // console.log("Login response:", response);

      // Handle 403 explicitly (expired/quota)
      if (response.status === 403) {
        const data = await response.json().catch(() => ({}));
        const msg = data?.message || 'You have exceeded the allowed capacity as a free user, please contact support@dollarbirdinc.com for your paid access.';
        setAuthBanner(msg);
        setAuthStatus('unauth');
        return;
      }

      if (!response.ok) {
        const errorData = await response.json();
        // console.log(errorData.message); 
        throw new Error(errorData.message);
      }

      const data = await response.json();
      const tk = data.access_token;

      const meRes = await fetch(`${baseUrl}/me`, {
        headers: { Authorization: 'Bearer ' + tk }
      });

      if (meRes.status === 403) {
        const info = await meRes.json().catch(() => ({}));
        const msg = info?.message || 'You have exceeded the allowed capacity as a free user, please contact support@dollarbirdinc.com for your paid access.';
        setAuthBanner(msg);
        setAuthStatus('unauth');
        return;
      }
      if (!meRes.ok) {
         const errorData = await meRes.json();
          // console.log(errorData.message); 
          throw new Error(errorData?.message||'Login token invalid');
        // throw new Error('Login token invalid')
      };

      const me = await meRes.json();

      localStorage.setItem("token", tk);
      localStorage.setItem("username", me.username || values?.username);

      setToken(tk);
      setAuthBanner('');
      setIsAdmin(Boolean(me.is_admin));
      setAuthStatus('auth');

      fetchMyUsage(tk);
    } catch (error) {
      console.log("Login error:", error);
      toast.error("Login failed: " + error.message);
      setAuthStatus('unauth');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('username');
    setToken('');
    setIsAdmin(false);
    setResults([]);
    setCsvData('');
    setSites('');
    setMasterAds('');
    setMyUsage(0);
    setAuthStatus('unauth');
  };

  const handleContactUsSubmit = async (values,resetForm) => {
    try {
      // console.log("handleContactUsSubmit values",values)
      // return;
      const response = await fetch(`${baseUrl}/contactus`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...values }),
      });
      // console.log("handleContactUsSubmit response:", response);


      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message);
      }

      const data = await response.json();
      console.log("data",data)
      resetForm()
      toast.success(data.message||"Thank you for contacting us! We’ll get back to you soon.")
    } catch (error) {
      console.log("Login error:", error);
      toast.error("Login failed: " + error.message);
      setAuthStatus('unauth');
    }
  };

  // Send verification code
  const handleSendCode = async () => {
    if (!isEmailValid) {
      toast.error('Please enter a valid email address.');
      return;
    }
    try {
      setCodeStatus('Sending code…');
      const res = await fetch(`${baseUrl}/signup/send-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      if (!res.ok) {
         const errorData = await res.json();
          // console.log(errorData.message); 
          throw new Error(errorData?.message||'Failed to send code');
        // const txt = await res.text();
        // throw new Error(txt || 'Failed to send code');
      }
      setCodeSent(true);
      setCodeStatus('Code sent! Please check your email. It expires in 5 minutes.');
    } catch (err) {
      setCodeStatus('');
      toast.error(err.message);
    }
  };

  // Sign up with password + verify_code
  const handleSignUp = async () => {
    if (!firstName.trim() || !lastName.trim()) {
      toast.error('Please enter your first and last name.');
      return;
    }
    if (!signupPassword || !signupPasswordConfirm) {
      toast.error('Please set your password and re-enter it.');
      return;
    }
    if (signupPassword !== signupPasswordConfirm) {
      toast.error('Passwords do not match.');
      return;
    }
    if (signupPassword.length < 6) {
      toast.error('Password must be at least 6 characters long.');
      return;
    }

    // Company -> must be a valid domain
    const normalizedDomain = extractDomain(company);
    if (!normalizedDomain || !DOMAIN_REGEX.test(normalizedDomain)) {
      toast.error('Please enter a valid company domain (e.g. example.com).');
      return;
    }

    if (!isEmailValid) {
      toast.error('Please enter a valid email address.');
      return;
    }
    if (!codeSent || !verifyCode.trim() || verifyCode.trim().length !== 6) {
      toast.error('Please request a code and enter the 6-digit verification code sent to your email.');
      return;
    }

    try {
      const res = await fetch(`${baseUrl}/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: firstName,
          last_name: lastName,
          email,
          company: normalizedDomain,   // send normalized domain
          user_type: 'user',
          verify_code: verifyCode.trim(),
          password: signupPassword,
          password_confirm: signupPasswordConfirm
        })
      });

      if (!res.ok) {
         const errorData = await res.json();
          // console.log(errorData.message); 
          throw new Error(errorData?.message||'Signup failed');
        // const data = await res.json().catch(() => ({}));
        // throw new Error(data?.detail || 'Signup failed');
      }

      await res.json();
      setSignUpSuccess(true);
      setShowSignUp(false);

      // reset signup fields
      setFirstName('');
      setLastName('');
      setSignupPassword('');
      setSignupPasswordConfirm('');
      setCompany('');
      setEmail('');
      setVerifyCode('');
      setCodeSent(false);
      setCodeStatus('');

    } catch (error) {
      toast.error("Signup failed: " + error.message);
    }
  };

  const handleCheck = async () => {
    let domains = sites.split('\n').map(site => site.trim()).filter(Boolean);
    if (isAppUrl) {
      domains = domains.map(d => d.endsWith('/') ? d + 'app-ads.txt' : d + '/app-ads.txt');
    }
    const masterLines = masterAds.split('\n').map(line => line.trim()).filter(Boolean);

    try {
      const res = await fetch(`${baseUrl}/check`, {
        method: 'POST',
        headers: {
          'Authorization': 'Bearer ' + token,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ domains, master_ads: masterLines })
      });

      // If quota/duration exceeded, send to login with banner
      if (res.status === 403) {
        const data = await res.json().catch(() => ({}));
        const msg = data?.message || 'You have exceeded the allowed capacity as a free user, please contact support@dollarbirdinc.com for your paid access.';
        // clear session
        localStorage.removeItem('token');
        localStorage.removeItem('username');
        setToken('');
        setIsAdmin(false);
        setResults([]);     // keep safe
        setCsvData('');
        setSites('');
        setMasterAds('');
        setAuthBanner(msg);
        setAuthStatus('unauth');
        return;
      }

      if (!res.ok) {
         const errorData = await res.json();
          // console.log(errorData.message); 
          throw new Error(errorData?.message||'Failed to check ads.txt');
        // const txt = await res.text().catch(() => '');
        // throw new Error(txt || 'Failed to check ads.txt');
      }

      const data = await res.json();
      const safeResults = Array.isArray(data?.results) ? data.results : [];
      setResults(safeResults);

      const rows = [['Date Checked', 'Site', 'Total Present', 'Total Missing', 'Missing Line']];
      safeResults.forEach(result => {
        if (result.error) {
          rows.push([new Date().toISOString(), result.domain, 'ERROR', 'ERROR', result.error]);
        } else if (Array.isArray(result.missing_lines) && result.missing_lines.length > 0) {
          result.missing_lines.forEach(line => {
            rows.push([result.date_checked, result.domain, result.present, result.missing, line]);
          });
        } else {
          rows.push([result.date_checked, result.domain, result.present, result.missing, '']);
        }
      });
      setCsvData(rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n'));

      fetchMyUsage();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const downloadCSV = () => {
    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'ads_txt_report.csv';
    link.click();
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadName(file.name);
    const reader = new FileReader();
    reader.onload = () => setSites(reader.result);
    reader.readAsText(file);
  };

  const handleClear = () => {
    setSites('');
    setMasterAds('');
    setResults([]);
    setCsvData('');
    setIsAppUrl(false);
    setUploadName('');
  };

  const handleSignUpRoute=()=>{
    setAuthStatus("unauth")
    setShowSignUp(true)
    setShowContactUs(false)
  }
  const handleContactUsRoute=()=>{
    setAuthStatus("unauth")
    setShowSignUp(false)
    setShowContactUs(true)
  }

  // ---------- AUTH GATE ----------
  if (authStatus === 'checking') {
    return <div className="app-shell centered"><div className="card">Validating session…</div></div>;
  }

  if (authStatus === 'pricingpage') {
    return (<>
        <div className="app-shell centered">
          <Pricing handleSignUpRoute={handleSignUpRoute} handleContactUsRoute={handleContactUsRoute} />
        </div>
    </>);
  }

  if (authStatus === 'unauth') {
    return (
      <>
        <ToastContainer />
      <div className="app-shell centered">
        {/* Centered logo above login card */}
        <div className="login-logo">
          <img src={DBLogo} alt="Dollarbird Logo" className="unauth_logo" />
        </div>
        {/* Red banner when quota/duration exceeded */}
        {authBanner && (
          <div
            style={{
              background: '#ffe9e9',
              border: '1px solid #ffb3b3',
              color: '#a20000',
              padding: '10px 12px',
              borderRadius: 10,
              marginBottom: 12,
              maxWidth: 540,
              textAlign: 'center'
            }}
          >
            {authBanner}
          </div>
        )}

        <div className="card login-box">
          <h3 className="login-title">{showContactUs? "Contact Us" :(showSignUp ? "Sign Up" : "Ads.txt Checker Login")}</h3>
          {signUpSuccess && (
            <div className="success">
              Your login needs to be approved by Dollarbird admin, please drop a mail to support@dollarbirdinc.com for a quick approvals
            </div>
          )}
          {showContactUs?<>
            <div className="stack">
              <Formik
                initialValues={{ name: '', email: '', contact_number: '' }}
                validationSchema={ContactValidationSchema}
                onSubmit={(values,{resetForm}) => {
                  handleContactUsSubmit(values,resetForm)
                }}
              >
                {({resetForm}) => (
                  <Form>
                    <div className='mb-3'>
                      {/* <label htmlFor="name">Name</label><br /> */}
                      <Field name="name" type="text" className="form-control" placeholder="Enter name" />
                      <div className='small text-danger'>
                        <ErrorMessage name="name" />
                      </div>
                    </div>

                    <div className='mb-3'>
                      {/* <label htmlFor="email">Email</label><br /> */}
                      <Field name="email" type="email" className="form-control" placeholder="Enter valid email" />
                      <div className='small text-danger'>
                        <ErrorMessage name="email" />
                      </div>
                    </div>

                    <div className='mb-3'>
                      {/* <label htmlFor="phone">Phone Number</label><br /> */}
                      <Field name="contact_number" type="text" className="form-control" placeholder="Enter contact number" />
                      <div className='small text-danger'>
                        <ErrorMessage name="contact_number"/>
                      </div>
                    </div>
                    <div className='d-flex gap-3 mt-3'>
                      <button type="submit" className='btn btn-primary text-end'>Submit</button>
                      <button type='button' className="btn btn-link" onClick={() => {
                        setShowSignUp(false);
                        setShowContactUs(false)
                        setAuthStatus("unauth")
                        // cleanup contact fields when exiting
                        resetForm()
                      }}>
                        Back to Login
                      </button>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </>:<>
          {showSignUp ? (
            <div className="stack">
              {/* First + Last Name */}
              <div className="form-row">
                <input className="input" type="text" placeholder="First Name" value={firstName} onChange={e => setFirstName(e.target.value)} />
                <input className="input" type="text" placeholder="Last Name" value={lastName} onChange={e => setLastName(e.target.value)} />
              </div>

              {/* Set password + Re-enter password */}
              <div className="form-row">
                <input
                  className="input"
                  type="password"
                  placeholder="Set your password"
                  value={signupPassword}
                  onChange={e => setSignupPassword(e.target.value)}
                />
                <input
                  className="input"
                  type="password"
                  placeholder="Re-enter password"
                  value={signupPasswordConfirm}
                  onChange={e => setSignupPasswordConfirm(e.target.value)}
                />
              </div>

              {/* Company domain (enforced) */}
              <div className="form-row">
                <input
                  className="input"
                  type="text"
                  placeholder="Company domain (e.g. example.com)"
                  value={company}
                  onChange={e => {
                    const dom = extractDomain(e.target.value);
                    setCompany(dom);
                  }}
                  onBlur={() => setCompany(extractDomain(company))}
                />
              </div>

              {/* Email + Send code */}
              <div className="form-row">
                <input
                  className="input"
                  type="email"
                  placeholder="Company Email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                />
                <button
                  className="btn btn-primary"
                  type="button"
                  onClick={handleSendCode}
                  disabled={!isEmailValid}
                  title={!isEmailValid ? 'Enter a valid email first' : 'Send verification code'}
                  style={{ minWidth: 140 }}
                >
                  Send code
                </button>
              </div>
              {codeStatus && <div className="caption">{codeStatus}</div>}

              {/* Verification code entry */}
              <div className="form-row">
                <input
                  className="input"
                  type="text"
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  placeholder="6-digit verification code"
                  value={verifyCode}
                  onChange={e => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0,6))}
                  required
                />
              </div>

              <div className="actions">
                <button
                  className="btn btn-primary"
                  onClick={handleSignUp}
                  disabled={
                    !firstName.trim() ||
                    !lastName.trim() ||
                    !company.trim() ||
                    !isEmailValid ||
                    !signupPassword ||
                    !signupPasswordConfirm ||
                    signupPassword !== signupPasswordConfirm ||
                    !verifyCode || verifyCode.length !== 6
                  }
                >
                  Submit
                </button>
                <button className="btn btn-link" onClick={() => {
                  setShowSignUp(false);
                  // cleanup signup fields when exiting
                  setFirstName(''); setLastName('');
                  setSignupPassword(''); setSignupPasswordConfirm('');
                  setCompany(''); setEmail('');
                  setVerifyCode(''); setCodeSent(false); setCodeStatus('');
                  setAuthBanner('');
                }}>
                  Back to Login
                </button>
              </div>
              <p className="caption">You’ll appear in Admin → Pending Approvals (with Email Verified status).</p>
            </div>
          ) : (
            <div className="stack">
              <Formik
                initialValues={{ username: '', password: '' }}
                validationSchema={loginValidationSchema}
                onSubmit={(values,{resetForm}) => {
                  handleLogin(values,resetForm)
                }}
              >
                {({resetForm}) => (
                  <Form>
                    <div className='mb-3'>
                      {/* <label htmlFor="name">Name</label><br /> */}
                      <Field name="username" type="text" className="form-control" placeholder="Enter username" />
                      <div className='small text-danger'>
                        <ErrorMessage name="username" />
                      </div>
                    </div>

                    <div className='mb-3'>
                      {/* <label htmlFor="email">Email</label><br /> */}
                      <Field name="password" type="password" className="form-control" placeholder="Enter password" />
                      <div className='small text-danger'>
                        <ErrorMessage name="password" />
                      </div>
                    </div>
                    <div className='d-flex gap-3 mt-3'>
                      <button type="submit" className="btn btn-primary">Login</button>
                      <button type="button" className="btn btn-link" onClick={() => { 
                        setShowSignUp(true); 
                        setAuthBanner('');
                        setAuthStatus("pricingpage");
                        resetForm(); 
                      }}>Sign Up</button>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          )}</>}
        </div>

        {/* Powered-by footer under the login card */}
        <div className="login-footer mb-2">
          Powered by - <strong>Dollarbird Technologies Pvt Ltd</strong>
        </div>
      </div>
      </>
    );
  }

  // ---------- AUTHENTICATED ----------
  if (isAdmin) {
    return (
      <>
        <ToastContainer />
        
      <div className="app-shell">
        <Header handleLogout={handleLogout} />
        <AdminUI />
      </div>
      </>
    );
  }

  // USER APP
  const hasResults = Array.isArray(results) && results.length > 0; // safe guard

  return (
    <>
      <ToastContainer />
    <div className="app-shell">
      <Header handleLogout={handleLogout} />
        {(myUsage>=allowedSites) &&<div>
          <p className='p-3 bg-danger-subtle border-1 my-2 mx-3 rounded-4'>You have exceeded the allowed capacity as a free user, please contact support@dollarbirdinc.com for your paid access.</p>
        </div>}
      <div className='mx-3'>
      <h4 className="app-title mb-3" style={{ marginBottom: 0 }}>Ads.txt Checker</h4>

      <div className="d-flex gap-2">
        <div className="card" style={{ flex: 1, minWidth: 280 }}>
          <h4 style={{ marginTop: 0, color: 'var(--primary)' }}>Paste site URLs (one per line) or upload file</h4>
          <textarea className="textarea" value={sites} onChange={(e) => setSites(e.target.value)} />
          <div className="d-flex gap-2" style={{ marginTop: 10, alignItems: 'center', gap: 12 }}>
            <div className={`file-upload-wrapper ${myUsage>=allowedSites?"disable":""}`}>
              <label htmlFor="file-upload" className={"file-upload-label"}>Choose File</label>
              <input
                type="file"
                id="file-upload"
                className="file-upload-input"
                accept=".txt"
                onChange={handleFileUpload}
              />
              {/* {uploadName && (
                <span style={{ marginLeft: 10, fontSize: 13, opacity: 0.8 }}>{uploadName}</span>
              )} */}
            </div>

            <label className="d-flex" style={{ gap: 6 }}>
              <input type="checkbox" checked={isAppUrl} onChange={e => setIsAppUrl(e.target.checked)} />
              <span>App URL</span>
            </label>
          </div>
        </div>

        <div className="card" style={{ flex: 1, minWidth: 280 }}>
          <h4 style={{ marginTop: 0, color: 'var(--primary)' }}>Paste your master ads.txt lines</h4>
          <textarea className="textarea" value={masterAds} onChange={(e) => setMasterAds(e.target.value)} />
        </div>
      </div>

      <div className="actions">
        <button className="btn btn-primary" disabled={myUsage>=allowedSites} onClick={handleCheck}>Check</button>
        <button className="btn btn-link" disabled={myUsage>=allowedSites} onClick={handleClear}>Clear</button>
        {hasResults && <button className="btn btn-secondary" onClick={downloadCSV}>Download CSV</button>}
      </div>

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Date Checked</th>
              <th>Site</th>
              <th>Total Present</th>
              <th>Total Missing</th>
              <th>Missing Lines</th>
            </tr>
          </thead>
          <tbody>
            {hasResults ? results.map((r, i) => (
              <tr key={i}>
                <td>{r.date_checked || '-'}</td>
                <td>{r.domain}</td>
                <td>{r.present ?? '-'}</td>
                <td>{r.missing ?? '-'}</td>
                <td>
                  {Array.isArray(r.missing_lines) ? (
                    <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>
                      {r.missing_lines.join('\n')}
                    </pre>
                  ) : r.error}
                </td>
              </tr>
            )) : (
              <tr><td colSpan="5" className="caption">No results yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className='mb-2' style={{ fontSize: 14, marginTop: 6, color: '#003bc0',textAlign: 'center' }}>
            Powered by <strong>Dollarbird Technologies Pvt Ltd</strong>
          </div>
      </div>
      {/* Credits used badge */}
      <div className="badge text-dark" aria-label="credits-used">
        <strong>Credits used</strong>: {myUsage} <span style={{ opacity: 0.7 }}>(# of sites scanned)</span>
      </div>
    </div>
    </>
  );
}

export default App;
